import React, {useState} from 'react';
import {Button, Icon} from '@ezcater/tapas';
import {faChevronLeft} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';

import {useUserMenuModalContext} from '@/components/global/providers/UserMenuModalProvider';
import {EzcaterLogo} from '@/components/ui';
import Modal from '@/components/ui/Modal';
import useTracking from '@/hooks/useTracking';
import CorpAccountSelectionMenuButton from './CorpAccountSelectionMenuButton';
import CorpAccountSelectionOption from './CorpAccountSelectionOption';
import useCorpAccountSelection from './useCorpAccountSelection';

type CorpAccountSelectionMobileProps = {
  defaultOpen?: boolean;
};

const CorpAccountSelectionMobile: React.FC<CorpAccountSelectionMobileProps> = ({
  defaultOpen = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(defaultOpen);
  const {setUserMenuModalOpen} = useUserMenuModalContext();
  const {track} = useTracking();
  const {
    sortedCorpAccounts,
    currentCorpAccountUuid,
    changeSelectedCorpAccountUuid,
    selectedCorpAccount,
  } = useCorpAccountSelection();
  const {t} = useTranslation('app-bar');

  const handleMenuOpen = (value: boolean) => {
    if (value) {
      track('corp-account-switcher-opened', {
        page: window.location.pathname,
        url: window.location.href,
        misc_json: JSON.stringify({
          area: 'user-menu-mobile',
          account_count: sortedCorpAccounts.length,
          current_account_uuid: currentCorpAccountUuid,
        }),
      });
    }
    setIsMenuOpen(value);
  };

  // For back button: only close this modal
  const onBackButtonClick = () => {
    handleMenuOpen(false);
  };

  // For X button: close all modals (including UserMenuModal)
  const dismissMenu = () => {
    handleMenuOpen(false);
    setUserMenuModalOpen(false);
  };

  // For account selection: close both modals after handling selection
  const handleMobileAccountClick = (accountUuid: string) => async () => {
    const previousAccountUuid = currentCorpAccountUuid;
    const previousAccount = sortedCorpAccounts.find(acc => acc.uuid === previousAccountUuid);
    const newAccount = sortedCorpAccounts.find(acc => acc.uuid === accountUuid);

    await changeSelectedCorpAccountUuid(accountUuid);
    dismissMenu();

    track('corp-account-switched', {
      page: window.location.pathname,
      url: window.location.href,
      misc_json: JSON.stringify({
        area: 'user-menu-mobile',
        previous_account_uuid: previousAccountUuid,
        new_account_uuid: accountUuid,
        previous_account_name: previousAccount?.name || '',
        new_account_name: newAccount?.name || '',
        total_accounts: sortedCorpAccounts.length,
      }),
    });
  };

  if (sortedCorpAccounts.length <= 1) {
    return null;
  }

  return (
    <Modal
      ariaLabel={
        t('menus.corporateAccounts.accountPicker.menuLabel') ?? 'Corporate Account Select Menu'
      }
      onOpenChange={open => !open && dismissMenu()}
      open={isMenuOpen}
      slotProps={{
        content: {
          className: 'p-0 tablet:h-auto tablet:min-h-[480px] tablet:max-w-[480px] tablet:p-4',
        },
      }}
      title={
        <>
          <span className="sr-only">
            {t('menus.corporateAccounts.accountPicker.menuLabel') ??
              'Corporate Account Select Menu'}
          </span>
          <div aria-hidden="true" className="flex h-10 items-center px-4 pt-4">
            <EzcaterLogo className="h-[18px] w-[120px]" height={18} width={120} />
          </div>
        </>
      }
      trigger={
        <CorpAccountSelectionMenuButton
          aria-expanded={isMenuOpen}
          selectedCorpAccount={selectedCorpAccount}
          onClick={() => handleMenuOpen(true)}
        />
      }
    >
      <div className="relative">
        <div className="px-4 py-2">
          <Button
            className="flex w-full items-center justify-start gap-2 px-4 py-2"
            onClick={onBackButtonClick}
            variant="secondary"
          >
            <Icon icon={faChevronLeft} size="xsmall" />
            <span>{t('menus.corporateAccounts.accountPicker.mobileModal.backLabel')}</span>
          </Button>
        </div>
        <div
          aria-label={t('menus.corporateAccounts.accountPicker.listboxLabel')}
          className="flex flex-col gap-2 p-4 pt-0"
          role="listbox"
        >
          {sortedCorpAccounts.map(corpAccount => (
            <CorpAccountSelectionOption
              className="flex-1 self-stretch px-4 py-2"
              corpAccount={corpAccount}
              isSelected={currentCorpAccountUuid === corpAccount.uuid}
              key={corpAccount.uuid}
              onClick={handleMobileAccountClick(corpAccount.uuid)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default CorpAccountSelectionMobile;
